import Player from "@vimeo/player";

const DEFAULT_SLIDER_AUTOPLAY_SPEED_MS = 6000;

function init() {
	const fullWidthTeaserSliderContainerComponent = document.querySelectorAll(
		"[data-full-width-teaser-slider-container-component]"
	);

	fullWidthTeaserSliderContainerComponent.forEach((slider) => {
		if (slider.children.length <= 1) {
			initSingleSlide(slider.children[0]);
		} else {
			initSlider(slider);
		}
	});
}

function initSlider(slider) {
	const $slider = $(slider);
	const sliderContainer = slider.parentElement;

	const dataSliderProgress = sliderContainer.querySelector("[data-slider-progress]");
	const dataSliderProgressNumber = sliderContainer.querySelector("[data-slider-progress-number]");

	const updateCurrentSliderProgress = (currentSlide = $slider[0].slick.currentSlide + 1) => {
		dataSliderProgressNumber.innerText = `${currentSlide} | ${$slider[0].slick.slideCount}`;
	};

	const sliderConfiguration = {
		lazyLoad: "ondemand",
		arrows: false,
		speed: 600,
		autoplay: true,
		autoplaySpeed: DEFAULT_SLIDER_AUTOPLAY_SPEED_MS,
		pauseOnFocus: false,
		pauseOnHover: false,
	};

	$slider.on("init", (event, slick) => {
		dataSliderProgress.style.width = "100%";

		slider.querySelectorAll(".slick-slide").forEach((slide) => {
			initSlideContent(slider, slide);
		});

		setTimeout(() => {
			slick.$slides[0]?.videoPlayer?.play();
		}, 5);
	});
	$slider.slick(sliderConfiguration);
	updateCurrentSliderProgress();

	const transitionClasses = ["transition-[width]", "ease-linear", `duration-[${DEFAULT_SLIDER_AUTOPLAY_SPEED_MS}ms]`];

	$slider.on("beforeChange", (event, slick, currentSlide, nextSlide) => {
		slick.$slides[currentSlide]?.videoPlayer?.stop();

		updateCurrentSliderProgress(nextSlide + 1);
		// remove transition classes to make them instantly reset progress
		dataSliderProgress.classList.remove(...transitionClasses);
		dataSliderProgress.style.width = "0";
	});

	$slider.on("afterChange", (event, slick, currentSlide) => {
		slick.$slides[currentSlide]?.videoPlayer?.play();

		dataSliderProgress.classList.add(...transitionClasses);
		dataSliderProgress.style.width = "100%";
	});

	sliderContainer.querySelector("[data-previous-arrow]")?.addEventListener("click", () => {
		$slider.slick("slickPrev");
	});

	sliderContainer.querySelector("[data-next-arrow]")?.addEventListener("click", () => {
		$slider.slick("slickNext");
	});
}

function initSingleSlide(slide) {
	slide.classList.remove("hidden");

	const player = initVideoPlayer(slide);
	if (!player) return;

	player.setLoop(true);
	player.play();
}

function initSlideContent(slider, slide) {
	slide.classList.remove("hidden");

	const player = initVideoPlayer(slide);
	if (!player) return;
	const shouldPauseSlider = shouldUseSlickPause(player);

	player.setLoop(false);

	if (shouldPauseSlider) {
		player.on("ended", () => {
			$(slider).slick("slickNext");
		});
	}

	// (3) make VideoPlayer accessible to the slide
	slide.videoPlayer = {};
	slide.videoPlayer.play = () => {
		player.play();

		if (shouldPauseSlider) {
			$(slider).slick("slickPause");
		}
	};
	slide.videoPlayer.stop = () => {
		player.pause();
		player.setCurrentTime(0);

		if (shouldPauseSlider) {
			$(slider).slick("slickPlay");
		}
	};
}

function initVideoPlayer(slide) {
	// (1) check for video iframe
	const iframe = slide.getElementsByTagName("iframe")[0];
	if (!iframe || !iframe.getAttribute("id").startsWith("videoframe_")) return;

	// (2) detect provider and set iframe to URL
	const width = window.innerWidth;
	const mobileURL = iframe.dataset.srcMobile;
	const desktopURL = iframe.dataset.srcDesktop;
	const selectedURL = width < 720 ? mobileURL : desktopURL;

	const provider = detectVideoProvider(selectedURL);

	if (provider === "vimeo") {
		try {
			iframe.src = makeVimeoURL(selectedURL);
		} catch {
			return null;
		}

		const player = new Player(iframe);
		player.provider = "vimeo";
		player.supportsEnded = true;
		return player;
	}

	if (provider === "mux") {
		return createMuxPlayerController(slide, iframe, selectedURL);
	}

	return null;
}

function createMuxPlayerController(slide, iframe, selectedURL) {
	const playbackId = extractMuxPlaybackId(selectedURL);
	if (!playbackId) {
		return createMuxIframeController(iframe, selectedURL);
	}

	const fallbackController = createMuxIframeController(iframe, selectedURL);
	const video = createMuxVideoElement(slide, iframe);
	const sources = buildMuxMp4Sources(playbackId);
	let sourceIndex = 0;
	let fallbackActive = false;
	let isLoopEnabled = false;

	const applySource = () => {
		if (!sources[sourceIndex]) {
			activateFallback();
			return;
		}

		video.src = sources[sourceIndex];
		video.load();
	};

	const activateFallback = () => {
		if (fallbackActive) return;

		fallbackActive = true;
		video.classList.add("hidden");
		iframe.classList.remove("hidden");
		fallbackController.setLoop(isLoopEnabled);
	};

	video.addEventListener("error", () => {
		if (sourceIndex < sources.length - 1) {
			sourceIndex += 1;
			applySource();
			return;
		}

		activateFallback();
	});

	applySource();

	return {
		provider: "mux",
		supportsEnded: false,
		play() {
			if (fallbackActive) {
				fallbackController.play();
				return;
			}

			video.classList.remove("hidden");
			iframe.classList.add("hidden");
			const playPromise = video.play();
			if (playPromise && typeof playPromise.catch === "function") {
				playPromise.catch(() => {
					activateFallback();
					fallbackController.play();
				});
			}
		},
		pause() {
			if (fallbackActive) {
				fallbackController.pause();
				return;
			}

			video.pause();
		},
		setLoop(loopEnabled) {
			isLoopEnabled = loopEnabled;

			if (fallbackActive) {
				fallbackController.setLoop(loopEnabled);
				return;
			}

			video.loop = loopEnabled;
		},
		setCurrentTime(value) {
			if (fallbackActive) return;

			video.currentTime = value;
		},
		on() {},
	};
}

function createMuxIframeController(iframe, selectedURL) {
	let isLoopEnabled = false;

	const updateIframeSrc = (autoplay) => {
		try {
			iframe.src = makeMuxURL(selectedURL, {
				autoplay,
				loop: isLoopEnabled,
			});
		} catch {
			iframe.src = "";
		}
	};

	return {
		provider: "mux",
		supportsEnded: false,
		play() {
			updateIframeSrc(true);
		},
		pause() {
			updateIframeSrc(false);
		},
		setLoop(loopEnabled) {
			isLoopEnabled = loopEnabled;
		},
		setCurrentTime() {},
		on() {},
	};
}

function createMuxVideoElement(slide, iframe) {
	const existingVideo = slide.querySelector(`[data-mux-video-for="${iframe.id}"]`);
	if (existingVideo) {
		return existingVideo;
	}

	const video = document.createElement("video");
	video.dataset.muxVideoFor = iframe.id;
	video.className = iframe.className;
	video.classList.add("hidden");
	video.preload = "auto";
	video.controls = false;
	video.loop = false;
	video.muted = true;
	video.defaultMuted = true;
	video.playsInline = true;
	video.setAttribute("playsinline", "");
	video.setAttribute("muted", "");
	video.style.objectFit = "cover";

	iframe.parentNode.insertBefore(video, iframe);

	return video;
}

function extractMuxPlaybackId(passedUrl) {
	if (!passedUrl) return null;
	const normalizedUrl = passedUrl.replaceAll("&amp;", "&");

	try {
		const url = new URL(normalizedUrl);
		const segments = url.pathname.split("/").filter(Boolean);
		const rawPlaybackId = segments[0] === "v" ? segments[1] : segments[0];

		if (!rawPlaybackId) return null;

		const [playbackId] = rawPlaybackId.split(".");
		return playbackId || null;
	} catch {
		return null;
	}
}

function buildMuxMp4Sources(playbackId) {
	const candidates = window.innerWidth < 720 ? ["480p"] : ["720p", "480p"];

	return candidates.map((quality) => `https://stream.mux.com/${playbackId}/${quality}.mp4`);
}

function shouldUseSlickPause(player) {
	return player.supportsEnded === true;
}

// Helper: Detect video provider from URL
function detectVideoProvider(url) {
	if (!url) return null;
	if (url.includes("vimeo.com")) return "vimeo";
	if (url.includes("mux.com")) return "mux";
	return null;
}

function makeVimeoURL(passedUrl) {
	passedUrl = passedUrl.replaceAll("&amp;", "&");
	const url = new URL(passedUrl);
	const hash = url.searchParams.get("h");
	const searchElements = [
		`h=${hash}`,
		"dnt=1",
		"autoplay=0",
		"loop=0",
		"muted=1",
		"controls=0",
		"byline=1",
		"keyboard=0",
		"portrait=0",
		"title=0",
	];
	url.search = `?${searchElements.join("&")}`;
	return url;
}

function makeMuxURL(passedUrl, options = {}) {
	const { autoplay = false, loop = false } = options;

	passedUrl = passedUrl.replaceAll("&amp;", "&");
	const url = new URL(passedUrl);

	// MUX URL parameters for privacy and autoplay control
	url.searchParams.set("autoplay", autoplay ? "muted" : "false");
	url.searchParams.set("muted", "true");
	url.searchParams.set("playsinline", "true");
	url.searchParams.set("loop", loop ? "true" : "false");
	url.searchParams.set("controls", "false");
	// Both dash and underscore variants are set for Mux API compatibility across embed versions
	url.searchParams.set("disable-cookies", "true");
	url.searchParams.set("disable-tracking", "true");
	url.searchParams.set("disable_cookies", "1");
	url.searchParams.set("disable_tracking", "1");

	return url;
}

export { init };
